import { yupResolver } from '@hookform/resolvers/yup'
import { isBefore } from 'date-fns'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Divider } from 'shared/components/Base/Divider'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Grid } from 'shared/components/Base/Grid'
import { Button } from 'shared/components/Button/Button'
import { useUserProvider } from 'shared/components/Context/UserContext'
import { DatePicker } from 'shared/components/DateInput/DatePicker'
import { TimePicker } from 'shared/components/DateInput/TimePicker'
import { Text } from 'shared/components/Text/Text'
import { TextInput } from 'shared/components/TextInput/TextInput'
import { bgGray, darkGray, lightGray1, primaryBlack } from 'shared/theme/colors'
import styled from 'styled-components'
import { mixed, object, string } from 'yup'

import { formatDate } from '../../helpers/formatters'
import { Handler } from '../../helpers/typeHelper'
import { createDeductionCredit, DeductionCreditReason } from '../../models/repayments'
import { Box } from '../Box/Box'

interface PolicyRefinanceFormValues {
  startDate: Date | null
  startTime: Date | null
  weeklyDeduction: string
}

interface PolicyRefinanceFormProps {
  policyId: string
  isSubscription: boolean
  totalRemainingBalance: number
  onCancel: Handler
  onPolicyDetail: Handler
  isVendorUser: boolean
}

function getNextMonday() {
  var d = new Date()
  d.setDate(d.getDate() + ((1 + 7 - d.getDay()) % 7))
  return d
}

function isDateMonday(date: Date) {
  return date.getDay() === 1
}

export const startTimeFormatter = (date: Date, time: Date) => {
  const dateObj = new Date(`${formatDate(date)} ${time.getHours()}:${time.getMinutes()}:00`)
  return new Date(dateObj).toISOString()
}

const PolicyRefinanceFormSchema = (isVendorUser: boolean) =>
  object().shape({
    startDate: mixed().test(function (value) {
      if (!value) {
        return this.createError({ message: 'Start date is required' })
      }

      const startTime = this.parent.startTime ?? new Date()
      const date = startTimeFormatter(value, startTime)

      if (isBefore(new Date(date), new Date())) {
        return this.createError({ message: 'Start date cannot be in the past' })
      }

      if (isVendorUser && !isDateMonday(value)) {
        return this.createError({ message: 'Start date must be a Monday' })
      }

      return true
    }),
    startTime: mixed().test(function (value) {
      if (!value) {
        return this.createError({ message: 'Start time is required' })
      }

      const startDate = this.parent.startDate ?? new Date()
      const date = startTimeFormatter(startDate, value)

      if (isBefore(new Date(date), new Date())) {
        return this.createError({ message: 'Start date cannot be in the past' })
      }

      return true
    }),
    weeklyDeduction: string().test(function (value) {
      if (!value) {
        return this.createError({ message: 'New weekly deduction is required' })
      }

      if (isNaN(Number(value))) {
        return this.createError({ message: 'Enter a valid number' })
      }

      return true
    }),
  })

export function PolicyRefinanceForm({
  policyId,
  isSubscription,
  totalRemainingBalance,
  onCancel,
  onPolicyDetail,
  isVendorUser,
}: PolicyRefinanceFormProps) {
  const { showBanner } = useUserProvider()
  const [isLoading, setIsLoading] = React.useState<boolean>(false)

  const { handleSubmit, control, setValue, watch } = useForm<PolicyRefinanceFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      startDate: getNextMonday(),
      startTime: new Date(new Date().setHours(0, 0, 0, 0)),
      weeklyDeduction: '',
    },
    resolver: yupResolver(PolicyRefinanceFormSchema(isVendorUser)),
  })

  async function onSubmit(data: PolicyRefinanceFormValues) {
    const { startDate, startTime, weeklyDeduction } = data

    setIsLoading(true)

    const dateScheduled = !!startDate && !!startTime ? startTimeFormatter(startDate, startTime) : undefined

    // TODO: Try catch block is not working here, catch block is not catching the api errors
    await createDeductionCredit(
      {
        reason: DeductionCreditReason.REFINANCE,
        periodic_amount: weeklyDeduction,
        date_scheduled: dateScheduled,
      },
      policyId
    )
      .then(() => {
        onPolicyDetail()
      })
      .catch((error) => {
        showBanner('error', { message: error?.response?.data?.data ?? 'Something went wrong.' })
      })

    setIsLoading(false)
  }

  const weeklyDeduction = watch('weeklyDeduction')
  const startDate = watch('startDate')
  const startTime = watch('startTime')

  useEffect(() => {
    // For vendor user, when start date is selected, set the start time to 00:00, and disable change
    if (isVendorUser && startDate && !startTime) {
      const newStartDate = new Date(startDate)
      newStartDate.setHours(0, 0, 0, 0)
      setValue('startTime', newStartDate)
    }
  }, [startDate, startTime, isVendorUser, setValue])

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column' }}>
        <Text variant="header3" fontColor={primaryBlack} mb={4} align="left">
          Enter refinancing details
        </Text>

        <Grid
          columns="minmax(0, 1fr) minmax(0, 180px)"
          smColumns="minmax(0, 1fr) minmax(0, 170px)"
          mb={3}
          styles="max-width: 424px;"
        >
          <Controller
            render={({ field, fieldState }) => (
              <StyledDatePicker
                {...field}
                label="Start date"
                error={fieldState.error?.message}
                disablePast
                shouldDisableDate={(date) =>
                  // Disable all dates except Monday for vendor user
                  isVendorUser && !isDateMonday(date)
                }
              />
            )}
            name="startDate"
            control={control}
          />

          <Controller
            render={({ field, fieldState }) => (
              <StyledTimePicker {...field} label="Start time (24 Hour)" error={fieldState.error?.message} />
            )}
            name="startTime"
            control={control}
            disabled={isVendorUser}
          />
        </Grid>

        <Controller
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              label="New weekly deduction"
              error={fieldState.error?.message}
              backgroundColor={bgGray}
              inputMode="numeric"
              isCurrency
            />
          )}
          control={control}
          name="weeklyDeduction"
        />

        {!isSubscription && (
          <FlexBox isFullWidth alignItems="center" gap="30px">
            <Text variant="header3" fontColor={lightGray1} styles="whitespace: nowrap;">
              Estimated billing periods:
            </Text>
            {!!weeklyDeduction && !isNaN(Number(weeklyDeduction)) && Number(weeklyDeduction) !== 0 ? (
              <Text variant="header3" fontColor={darkGray}>
                {Math.ceil(totalRemainingBalance / Number(weeklyDeduction))} weeks
              </Text>
            ) : (
              <Divider width="75px" />
            )}
          </FlexBox>
        )}

        <Box mt={8} smMt={3} />

        <FlexBox
          isFullWidth
          justifyContent="flex-end"
          smJustifyContent="center"
          gap="24px"
          styles="margin-top: auto"
        >
          <Button variant="text" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" isLoading={isLoading}>
            Confirm
          </Button>
        </FlexBox>
      </form>
    </>
  )
}

const StyledTimePicker = styled(TimePicker)`
  background-color: ${bgGray};
  width: 100% !important;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
`

const StyledDatePicker = styled(DatePicker)`
  background-color: ${bgGray};
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
`
